import type { Fleet, FleetsView, Planet, PlayerPremium, SendFleetInput } from '@kaeliux/shared';
import { PlayerPremiumSchema, toDate } from '@kaeliux/shared';
import { authedPost } from './http';
import { normalizePlanet } from './planetNormalization';

/**
 * Fleet service — real backend flavour. Reads AND mutations both go through
 * Cloud Function endpoints (not direct Firestore reads like other services):
 * fleet checkpoints resolve lazily server-side, so even "just listing" the
 * fleets must run `/getFleets` to process any due arrival/return first.
 * Mock mode swaps this file for `services/mock/fleet.service.ts` via the
 * Vite alias in `vite.config.ts`.
 */

export function normalizeFleet(raw: unknown): Fleet {
    const data = raw as Record<string, unknown>;
    return {
        ...data,
        departedAt: toDate(data.departedAt),
        arrivesAt: toDate(data.arrivesAt),
        returnsAt: toDate(data.returnsAt),
        createdAt: toDate(data.createdAt),
        updatedAt: toDate(data.updatedAt),
    } as Fleet;
}

function normalizeView(raw: FleetsView): FleetsView {
    return { ...raw, fleets: (raw.fleets as unknown[]).map(normalizeFleet) };
}

export interface SendFleetResult extends FleetsView {
    fleet: Fleet;
    /** Origin planet after ships/cargo/gas were deducted. */
    planet: Planet;
    /** Present only when a premium speed boost was bought — the new Dark Matter balance. */
    premium?: PlayerPremium;
}

/**
 * Accepted (but ignored) by the real backend flavour — the server derives
 * the universe's flight multiplier itself. Mock mode consumes it so its
 * dispatch uses the SAME multiplier the page's estimates display.
 */
export interface FleetActionOptions {
    flightMultiplier?: number;
}

export async function getFleets(originPlanetId?: string): Promise<FleetsView> {
    const data = await authedPost<FleetsView>('/getFleets', originPlanetId ? { originPlanetId } : {});
    return normalizeView(data);
}

export async function sendFleet(input: SendFleetInput, _options?: FleetActionOptions): Promise<SendFleetResult> {
    const data = await authedPost<SendFleetResult & { planet: unknown; premium?: unknown }>('/sendFleet', input);
    return {
        ...normalizeView(data),
        fleet: normalizeFleet(data.fleet),
        planet: normalizePlanet(data.planet, input.originPlanetId),
        ...(data.premium !== undefined ? { premium: PlayerPremiumSchema.parse(data.premium) } : {}),
    };
}

export async function recallFleet(fleetId: string): Promise<Fleet> {
    const data = await authedPost<{ fleet: unknown }>('/recallFleet', { fleetId });
    return normalizeFleet(data.fleet);
}
